import Button from '@components/Button/Button';
import Popover, { type PositionerOptions, positionBounded } from '@components/Popover/Popover';
import { ButtonsSection, ContentSection } from '@components/Popover/PopoverLayouts';
import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons';
import { faTimes, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'motion/react';
import { memo } from 'react';

import styles from './EditCardPopover.module.scss';

type FieldType = 'field' | 'picture';
type EditCardPopoverProps = {
  adaptive?: boolean;
  drawFocus?: boolean;
  fieldType: FieldType;
  onCancelDeleteWarning?: () => void;
  onCancelUnlink?: () => void;
  onClose: () => void;
  onConfirmUnlink?: () => void;
  onDelete?: () => void;
  onSubmit?: (e: React.FormEvent) => void;
  positionDeps?: React.DependencyList;
  positionerOptions?: PositionerOptions;
  /** Passed to the underlying popover */
  preventCloseOnClickOutsidePopover?: boolean;
  show: boolean;
  showDeleteWarning?: boolean;
  showUnlinkWarning?: boolean;
  targetNode: HTMLElement | undefined;
  unlinking?: boolean;
  unlinkWarningMessage?: string;
};

const DeleteWarning: React.FC<
  React.PropsWithChildren<{
    fieldType: FieldType;
    onCancelDeleteWarning?: () => void;
    onDelete?: () => void;
  }>
> = ({ fieldType, onCancelDeleteWarning, onDelete }) => {
  return (
    <>
      <ContentSection>
        <div className={styles['delete-image-content-container']}>
          <div className={styles['delete-notification-icon']}>
            <div className={styles['delete-icon-holder']}>
              <FontAwesomeIcon icon={faTimes} color="white" />
            </div>
          </div>
          {fieldType === 'picture' && (
            <>
              <h3 className={styles['action-view-heading']}>Remove this image?</h3>
              <p>This image will be permanently removed</p>
            </>
          )}
          {fieldType === 'field' && (
            <>
              <h3 className={styles['action-view-heading']}>Remove this field?</h3>
              <p>The information will be removed</p>
            </>
          )}
        </div>
      </ContentSection>
      <ButtonsSection>
        <div className={styles['action-btns-container']}>
          <div className={styles['controls-section-delete-btns']}>
            <Button variant="subtle" size="medium" onClick={onCancelDeleteWarning}>
              Cancel
            </Button>
            <Button variant="primary" size="medium" type="danger" onClick={onDelete}>
              Remove
            </Button>
          </div>
        </div>
      </ButtonsSection>
    </>
  );
};

const EditCardPopover: React.FC<React.PropsWithChildren<EditCardPopoverProps>> = memo(
  ({
    fieldType,
    adaptive,
    preventCloseOnClickOutsidePopover,
    children,
    drawFocus = false,
    onCancelDeleteWarning,
    onCancelUnlink,
    onClose,
    onConfirmUnlink,
    onDelete,
    onSubmit,
    positionDeps = [],
    positionerOptions,
    show,
    showDeleteWarning,
    showUnlinkWarning,
    targetNode,
    unlinking = false,
    unlinkWarningMessage,
  }) => {
    return (
      <Popover
        positioner={positionBounded(
          positionerOptions || {
            offsetX: 8,
            xPreference: 'right',
            alignY: 'cover',
            adaptive: adaptive ?? true,
            bindToViewport: true,
          },
        )}
        targetNode={targetNode}
        show={show}
        onClose={onClose}
        preventOutsideInteraction
        preventCloseOnClickOutsidePopover={preventCloseOnClickOutsidePopover}
        positionDeps={[showDeleteWarning, showUnlinkWarning, ...positionDeps]}
        drawFocus={drawFocus}
      >
        <div className={styles['edit-card-popover']}>
          {showDeleteWarning && (
            <DeleteWarning
              fieldType={fieldType}
              onCancelDeleteWarning={onCancelDeleteWarning}
              onDelete={onDelete}
            />
          )}
          {showUnlinkWarning && onCancelUnlink && onConfirmUnlink && (
            <ContentSection>
              <motion.div className={styles['action-view']} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className={styles['notification-icon']}>
                  <FontAwesomeIcon icon={faExclamationCircle} />
                </div>
                <h3 className={styles['action-view-heading']}>Are you sure?</h3>
                {unlinkWarningMessage && (
                  <p className={styles['action-view-explanation']}>{unlinkWarningMessage}</p>
                )}

                <div className={styles['action-view-action-btns']}>
                  <Button variant="subtle" size="medium" onClick={onCancelUnlink}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="medium"
                    type="primary"
                    onClick={onConfirmUnlink}
                    loading={unlinking}
                  >
                    Save and unlink
                  </Button>
                </div>
              </motion.div>
            </ContentSection>
          )}
          {!showDeleteWarning && !showUnlinkWarning && !onSubmit && (
            <div className={styles['popover-body']}>{children}</div>
          )}
          {!showDeleteWarning && !showUnlinkWarning && onSubmit && (
            <form onSubmit={onSubmit} className={styles['popover-body']}>
              {children}
            </form>
          )}
        </div>
      </Popover>
    );
  },
);

interface EditCardControlsSectionProps {
  canDelete: boolean;
  onDeletePress: () => void;
}

const EditCardControlsSection: React.FC<React.PropsWithChildren<EditCardControlsSectionProps>> = ({
  children,
  canDelete,
  onDeletePress,
}) => {
  return (
    <>
      <div className={styles['action-btns-container']}>
        <div className={styles['controls-section-delete-btn-container']}>
          {canDelete && (
            <Button variant="text" type="default" size="medium" onClick={onDeletePress}>
              <FontAwesomeIcon icon={faTrash} size="lg" />
            </Button>
          )}
        </div>
        <div className={styles['controls-section-other-btns']}>{children}</div>
      </div>
    </>
  );
};

EditCardPopover.displayName = 'EditCardPopover';

export const Panel: React.FC<React.PropsWithChildren<{ variant?: 'normal' | 'tight' }>> = ({
  children,
  variant,
}) => {
  switch (variant) {
    case 'tight': {
      return <div className={styles['panel-tight']}>{children}</div>;
    }
    default:
      return <div className={styles['panel']}>{children}</div>;
  }
};

export const StackedPanels: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  return <div className={styles['stacked-panels']}>{children}</div>;
};

export default EditCardPopover;
export { EditCardControlsSection };
